import * as React from 'react'
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid2 as Grid, MenuItem, TextField } from '@mui/material'
import useFetchWithAuth from 'hooks/useFetchWithAuth'
import { ErrorContext } from 'contexts/errorContext'

/**
 * ScheduleForm component for displaying schedule form in a dialog
 */
const ScheduleForm = ({ open, order, onSuccess, onClose }) => {
  const initFormData = { orderNo: '', interval: '' }
  const [formData, setFormData] = React.useState(initFormData)
  const [formErrors, setFormErrors] = React.useState({})
  const [alert, setAlert] = React.useState(null)
  const [isLoading, setIsLoading] = React.useState(false)
  const { showError } = React.useContext(ErrorContext)
  const fetchWithAuth = useFetchWithAuth()

  React.useEffect(() => {
    if (!open) {
      setFormData(initFormData)
      setFormErrors({})
      setAlert(null)
    }

    if (open && order) {
      setFormData({ orderNo: order.orderNo, interval: order.interval })
    }
  }, [open])

  // Handles form changes
  const handleChange = (e) => {
    const { name, value } = e.target

    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault()

    setFormErrors({})
    setAlert(null)
    setIsLoading(true)

    try {
      const request = order
        ? { url: `/api/orders/${order._id}/schedule`, method: 'PATCH' }
        : { url: `/api/orders/schedule`, method: 'POST' }

      const response = await fetchWithAuth(request.url, {
        method: request.method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      })
      
      const json = await response.json()

      if (response.ok) {
        if (!order) setFormData(initFormData)
        setAlert({ type: 'success', message: json.message + '!' })
        onSuccess()
      } else {
        if ('errors' in json) {
          setFormErrors(json.errors)
        } else {
          setAlert({ type: 'error', message: 'Something went wrong. Please try again.' })
        }
      }
    } catch (error) {
      showError('Failed to save schedule. Please try again or refresh the page.')
      console.error(error.message)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{order ? 'Update Schedule' : 'Set Schedule'}</DialogTitle>
      <DialogContent>
        {alert && (
          <Alert severity={alert.type} sx={{ mb: 3, border: 1, borderColor: alert.type + '.light' }}>
            {alert.message}
          </Alert>
        )}
        <Box component="form" onSubmit={handleSubmit} id="order-schedule-form" sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            <Grid size={12}>
              <TextField fullWidth variant="outlined" type="text" label="Order No." name="orderNo"
                value={formData.orderNo} onChange={handleChange}
                error={!!formErrors.orderNo} helperText={formErrors.orderNo}
                readOnly={Boolean(order)}
              />
            </Grid>
          </Grid>

          <Grid container spacing={3} sx={{ mt: 3 }}>
            <Grid size={12}>
              <TextField select fullWidth variant="outlined" label="Interval" name="interval"
                value={formData.interval} onChange={handleChange}
                error={!!formErrors.interval} helperText={formErrors.interval}
              >
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="every_two_weeks">Every two weeks</MenuItem>
                <MenuItem value="every_three_weeks">Every three weeks</MenuItem>
                <MenuItem value="every_four_weeks">Every four weeks</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 4, gap: 1 }}>
        <Button variant="contained" color="secondary" onClick={onClose}>
          Close
        </Button>
        <Button variant="contained" type="submit" form="order-schedule-form" disabled={isLoading}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ScheduleForm